import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FeatureToggle from '~/constants/featureToggle'
import InvoiceType from '~/constants/InvoiceType'
import { convertToLineItems } from '~/store/actions/finance'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Estimate, Order } from '~/types'
import { convertOrderToInvoiceLineItem } from '~/utils/orderUtils'

interface UseEstimateCheckItemProps {
  clientId: string
  estimate: Estimate
  loadPrescriptionTemplate?: (order: Order) => void
  logItemStateGetter: (order: Order, state?: string) => Order | null
  onSelectItem?: (
    selectedItem: string | undefined,
    meta?: Record<string, any>,
  ) => void
  patientId: string
}

export const useEstimateCheckItem = ({
  estimate,
  clientId,
  patientId,
  logItemStateGetter,
  onSelectItem,
  loadPrescriptionTemplate,
}: UseEstimateCheckItemProps) => {
  const dispatch = useDispatch()
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  return useCallback(
    (order: Order, { isPrescribable }: { isPrescribable?: boolean } = {}) => {
      onSelectItem?.(order.variation?.id, { name: order.name })

      if (isPrescribable) {
        // For estimates, load prescription templates instead of opening dialog
        if (loadPrescriptionTemplate) {
          loadPrescriptionTemplate(order)
        }
      } else {
        const newOrder =
          order?.usedQuantity === 0 ? { ...order, usedQuantity: 1 } : order
        const preparedNewOrder = logItemStateGetter(newOrder)

        if (preparedNewOrder) {
          const lineItem = convertOrderToInvoiceLineItem(
            preparedNewOrder,
            isFoodCatalogEnabled,
          )

          dispatch(
            convertToLineItems({
              invoiceId: estimate.id,
              clientId,
              patientId,
              invoiceType: InvoiceType.ESTIMATE.toUpperCase(),
              otcItems: [lineItem],
              // Use discount only for bundles
              bundleDiscount: 0,
            }),
          )
        }
      }
    },
    [
      estimate.id,
      clientId,
      patientId,
      dispatch,
      logItemStateGetter,
      onSelectItem,
      isFoodCatalogEnabled,
      loadPrescriptionTemplate,
    ],
  )
}
